/**
 * ManagedSqlDbDetailsModal Component
 *
 * Modal for viewing managed SQL database details.
 */

import React from 'react';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Alert } from '../../../components/ui/Alert';
import { Spinner } from '../../../components/ui/Spinner';
import { CopyableCode } from '../../../components/ui/CopyableCode';
import { ManagedSqlDbStatusBadge } from './ManagedSqlDbStatusBadge';
import { useManagedSqlDbDetails } from '../hooks/useManagedSqlDbDetails';
import { formatDate } from '../../../lib/utils/format';
import { getErrorMessage } from '../../../lib/utils/errors';
import { ManagedSqlDbStatus } from '../../../types/managed-sql-db.types';

interface ManagedSqlDbDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  managedSqlDbId: string;
}

/**
 * ManagedSqlDbDetailsModal component
 */
export const ManagedSqlDbDetailsModal: React.FC<ManagedSqlDbDetailsModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  managedSqlDbId,
}) => {
  const { data: managedSqlDb, isLoading, error } = useManagedSqlDbDetails(tenantId, managedSqlDbId);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <Modal.Header>Managed SQL Database Details</Modal.Header>
      <Modal.Body>
        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        )}

        {/* Error State */}
        {error && <Alert variant="error">{getErrorMessage(error)}</Alert>}

        {/* Database Details */}
        {managedSqlDb && (
          <div className="space-y-6">
            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Database Name</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {managedSqlDb.managedSqlDbName}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Database Code</dt>
                  <dd className="mt-1">
                    <CopyableCode value={managedSqlDb.managedSqlDbCode} />
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Status</dt>
                  <dd className="mt-1">
                    <ManagedSqlDbStatusBadge
                      status={managedSqlDb.status}
                      statusMessage={managedSqlDb.statusMessage}
                    />
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">PostgreSQL Database</dt>
                  <dd className="mt-1">
                    <CopyableCode value={managedSqlDb.databaseName} />
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Database ID</dt>
                  <dd className="mt-1">
                    <CopyableCode value={managedSqlDb.managedSqlDbId} />
                  </dd>
                </div>
                {managedSqlDb.description && (
                  <div className="sm:col-span-2">
                    <dt className="text-sm font-medium text-gray-500">Description</dt>
                    <dd className="mt-1 text-sm text-gray-900">{managedSqlDb.description}</dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Status Message (if failed) */}
            {managedSqlDb.status === ManagedSqlDbStatus.FAILED && managedSqlDb.statusMessage && (
              <Alert variant="error">
                <strong>Error:</strong> {managedSqlDb.statusMessage}
              </Alert>
            )}

            {/* Metadata */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Metadata</h3>
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Created At</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formatDate(managedSqlDb.createdAt, 'MMM dd, yyyy HH:mm')}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Updated At</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formatDate(managedSqlDb.updatedAt, 'MMM dd, yyyy HH:mm')}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
